/**
 * Runtime configuration.
 *
 * Feature flags exist in two places on purpose: this file is the build-time
 * switch (so a disabled feature costs no bundle), and the `feature_flags` table
 * is the runtime switch (so it can be turned off without a deploy). A feature
 * is live only when both agree.
 */

function bool(value: string | undefined, fallback = false): boolean {
  if (value === undefined) return fallback
  return value === 'true' || value === '1'
}

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      'Falta la variable de entorno ' + name + '. Copia .env.example a .env y rellénala.',
    )
  }
  return value
}

export const env = {
  supabaseUrl: required('VITE_SUPABASE_URL', import.meta.env.VITE_SUPABASE_URL),
  supabaseAnonKey: required('VITE_SUPABASE_ANON_KEY', import.meta.env.VITE_SUPABASE_ANON_KEY),
  vapidPublicKey: import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined,
  /**
   * Raster tiles. OpenStreetMap's own tiles need no key (CARTO's stopped
   * being keyless in August 2026 — they now watermark 'API KEY REQUIRED').
   * Night mode reuses the same tiles under a CSS dusk filter, so both
   * entries point at the same set; both stay configurable for a future
   * provider swap.
   */
  mapTilesLight:
    (import.meta.env.VITE_MAP_TILES_LIGHT as string | undefined) ??
    'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  mapTilesDark:
    (import.meta.env.VITE_MAP_TILES_DARK as string | undefined) ??
    'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  mapAttribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
} as const

export const flags = {
  video: bool(import.meta.env.VITE_ENABLE_VIDEO, false),
  aiIdentity: bool(import.meta.env.VITE_ENABLE_AI_IDENTITY, true),
  aiDescription: bool(import.meta.env.VITE_ENABLE_AI_DESCRIPTION, true),
  push: bool(import.meta.env.VITE_ENABLE_PUSH, true),
  discover: bool(import.meta.env.VITE_ENABLE_DISCOVER, true),
} as const

export type FeatureFlag = keyof typeof flags

/** Limits mirrored from app_config. The database is the authority; these keep
 *  the interface honest before a request is made. */
export const limits = {
  imageMaxBytes: 12 * 1024 * 1024,
  videoMaxBytes: 8 * 1024 * 1024,
  videoMaxSeconds: 10,
  /** Longest edge after downscaling. Big enough to enlarge, small enough to send. */
  imageMaxEdge: 2048,
  thumbnailEdge: 480,
  imageQuality: 0.86,
  followMessageMax: 300,
  nameMax: 40,
  notesMax: 2000,
} as const

/**
 * The identity model, and its version.
 *
 * Embeddings are only ever compared within one model version, so changing this
 * string retires the old fingerprints instead of silently mixing two vector
 * spaces. DINOv2-small was chosen by measurement — it is the only configuration
 * tested that separates individual cats cleanly. See docs/identity-engine.md.
 */
export const IDENTITY_MODEL = 'dinov2-small-cls@1'
export const IDENTITY_DIMENSIONS = 384
